import type { ObjectLiteral } from 'api/submission';
import { type FormikProps, type FormikValues, useFormik } from 'formik';
import { isEmpty } from 'lodash-es';
import { useRef, useState } from 'react';
import * as yup from 'yup';
import type {
  QuestionEngineRef,
  UseQuestionEngineProps,
  UseQuestionEngineReturn,
} from './QuestionEngine.types';
import { addValidationsToDynamicFields } from './QuestionEngine.utils';

export const useQuestionEngine = <T extends ObjectLiteral>({
  initialState,
  customValidation = {},
  getAdditionalValidationFields,
  ...rest
}: UseQuestionEngineProps<T>): UseQuestionEngineReturn<T> => {
  const ref = useRef<QuestionEngineRef>(undefined);
  const [state, setState] = useState(initialState || ({} as T));

  const formik = useFormik({
    initialValues: {
      ...state,
    } as FormikValues,
    validationSchema: yup.lazy((values) => {
      const renderedFields = ref?.current?.renderedQuestions() ?? [];
      const additionalFields = getAdditionalValidationFields?.(values as T) ?? [];
      const allFields = [...renderedFields, ...additionalFields];

      return yup.object().shape({
        ...addValidationsToDynamicFields(allFields),
        ...customValidation,
      });
    }),
    onSubmit: (_values) => {},
  }) as unknown as FormikProps<T>;

  const validateFormik = async () => {
    await formik.submitForm();
    const errors = await formik.validateForm();

    if (!isEmpty(errors)) {
      await formik.setTouched(
        {
          ...formik.touched,
          ...Object.keys(errors).reduce((acc, key) => {
            acc[key] = true;
            return acc;
          }, {}),
        },
        false,
      );

      return false;
    }

    return true;
  };

  const updateState = (initialData: T) => {
    setState(initialData);
    formik.setValues(initialData);
  };

  return {
    state,
    setState,
    formik,
    validateFormik,
    updateState,
    ref,
    ...rest,
  };
};
